/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { observer } from 'mobx-react-lite'
import { useEffect, useLayoutEffect, useState } from 'react'
import { Select } from 'antd'
import InputMask from 'react-input-mask'

import { useMobxStore } from '../../../state'
import { EventCard } from './ConfirmProfile'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import colors from '@sportninja/common/constants/appColors'
import Icon from '../../../components/Icon'
import { toJS } from 'mobx'

const DropdownIndicator = ({ onClick, ...props }) => {
  return (
    <div
      onClick={onClick}
      css={css`
        width: 30px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      `}
      {...props.innerProps}
    >
      <Icon
        name='chevron-down'
        color='#898a8a'
        hoverColor={colors.SOFT_STEEL}
      />
    </div>
  )
}

const TeamControl = ({ onClick, ...props }) => {
  const value = props.hasValue
    ? props.getValue()[0]
    : { name: 'Select...', image: null }

  return (
    <div
      onClick={onClick}
      css={css`
        display: flex;
        align-items: center;
        padding: 8px;
        flex: 1;
      `}
    >
      {value?.image?.full_path && (
        <img
          src={value?.image?.full_path}
          css={css`
            width: 24px;
            height: 24px;
            margin-right: 8px;
          `}
        />
      )}
      <span
        css={css`
          font-size: 16px;
          opacity: ${value.name === 'Select...' ? 0.5 : 1};
        `}
      >
        {value.name}
      </span>
    </div>
  )
}

const TeamOptions = ({ onClick, innerRef, innerProps, data }) => {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      onClick={() => {
        onClick()
        innerProps.onClick()
      }}
      css={css`
        display: flex;
        align-items: center;
        padding: 12px 8px;
        cursor: pointer;
        background-color: transparent;
        transition: all 0.2s ease-in-out;

        &:hover {
          background-color: white;
        }
      `}
    >
      {data?.image?.full_path && (
        <img
          src={data?.image?.full_path}
          css={css`
            width: 24px;
            height: 24px;
            margin-right: 8px;
          `}
        />
      )}
      <span
        css={css`
          font-size: 16px;
        `}
      >
        {data.name}
      </span>
    </div>
  )
}

export const Input = ({
  label,
  value,
  useAsRow,
  onChange,
  placeholder = '',
  inputType = undefined,
  maxLength = undefined,
  inputMaxWidth = undefined,
  isDarkMode,
  hasError,
}) => (
  <div
    css={css`
      margin: ${!label ? '0' : '16px 0'};
      width: 100%;

      @media (max-width: 768px) {
        margin: 0;
        margin-bottom: 8px;
      }
    `}
  >
    {label && (
      <div
        css={css`
          margin-bottom: 8px;
        `}
      >
        <span
          css={css`
            font-size: 14px;
          `}
        >
          {label}
        </span>
      </div>
    )}
    {useAsRow ? (
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          padding: 8px;
          flex: 1;
          height: 40px;
          background-color: ${isDarkMode ? '#33363B' : '#f5f5f5'};
          border: 1px solid ${hasError ? colors.RED : colors.ATTENDANCE_GRAY};
          border-radius: 4px;
        `}
      >
        <span
          css={css`
            font-size: 16px;
          `}
        >
          {value}
        </span>
      </div>
    ) : (
      <div
        css={css`
          flex: 1;
          display: flex;
        `}
      >
        <input
          type={inputType}
          placeholder={placeholder}
          maxLength={maxLength}
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 8px;
            flex: 1;
            height: 40px;
            background-color: ${isDarkMode ? '#33363B' : '#f5f5f5'};
            border: 1px solid ${colors.ATTENDANCE_GRAY};
            color: ${isDarkMode ? colors.WHITE : colors.HEADER};
            border-radius: 4px;
            font-size: 16px;
            max-width: ${inputMaxWidth || '100%'};
          `}
          value={value}
          onChange={onChange}
        />
      </div>
    )}
  </div>
)

const SelectControl = ({ onClick, ...props }) => {
  const value = props.hasValue ? props.getValue()[0] : { name: 'Select...' }

  return (
    <div
      onClick={onClick}
      css={css`
        align-items: center;
        padding: 8px;
        display: flex;
        flex: 1;
      `}
    >
      <span
        css={css`
          font-size: 16px;
          opacity: ${value.name === 'Select...' ? 0.5 : 1};
        `}
      >
        {value.name || String(value.tier).split(' ')[0]}
      </span>
    </div>
  )
}

const SelectOptions = ({ onClick, innerRef, innerProps, data, isDarkMode }) => {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      onClick={() => {
        onClick()
        innerProps.onClick()
      }}
      css={css`
        padding: 12px 8px;
        cursor: pointer;
        background-color: transparent;
        transition: all 0.2s ease-in-out;

        &:hover {
          background-color: ${isDarkMode ? '#12161C' : 'white'};
        }
      `}
    >
      <span
        css={css`
          font-size: 16px;
          margin-left: 8px;
        `}
      >
        {data.name || data.tier}
      </span>
    </div>
  )
}

export const NewPlayerRegistration = observer(({ isDarkMode }) => {
  const {
    registrationInvite: {
      state: {
        eventInfo,
        userExtraInfo,
        playerTeams,
        skillLevels,
        selectedTeamPlayerRosterId,
      },
      confirmPlayerSelect,
      pushToStep,
    },
  } = useMobxStore()

  console.log('skillLevels', toJS(skillLevels))

  const [selectedTeam, setSelectedTeam] = useState({})
  const [number, setPlayerNumber] = useState('')
  const [position, setPlayerPosition] = useState(null)
  const [dateOfBirthday, setPlayerDateOfBirthday] = useState({
    year: '',
    month: '',
    day: '',
  })
  const [skillLevel, setPlayerSkillLevel] = useState(undefined)

  const [teamMenuIsOpen, setTeamMenuIsOpen] = useState(false)
  const [positionMenuIsOpen, setPositionMenuIsOpen] = useState(false)
  const [skillMenuIsOpen, setSkillMenuIsOpen] = useState(false)
  const [disabled, setDisabled] = useState(true)
  const [preselectedTeam, setPreselectedTeam] = useState(null)

  useEffect(() => {
    if (
      (selectedTeam?.id || preselectedTeam?.id) &&
      number !== '' &&
      position &&
      dateOfBirthday.day !== '' &&
      dateOfBirthday.month !== '' &&
      dateOfBirthday.year !== '' &&
      skillLevel
    ) {
      setDisabled(false)
    } else {
      setDisabled(true)
    }
  }, [selectedTeam, number, position, dateOfBirthday, skillLevel])

  useLayoutEffect(() => {
    if (selectedTeamPlayerRosterId) {
      const team = playerTeams?.find(
        (team) => team.team_player_roster_id === selectedTeamPlayerRosterId
      )

      if (team) {
        setPreselectedTeam(team)
      }
    }
  }, [selectedTeamPlayerRosterId])

  return (
    <div
      css={css`
        color: ${isDarkMode ? colors.WHITE : colors.BLACK};

        .ant-select-selector {
          background-color: ${isDarkMode ? '#33363B' : '#f5f5f5'} !important;
          border: 1px solid ${colors.ATTENDANCE_GRAY} !important;
        }
      `}
    >
      <button
        onClick={() => pushToStep(0)}
        css={css`
          padding-top: 16px;
          align-items: center;
          display: flex;
          gap: 16px;
          align-self: flex-start;
        `}
      >
        <Icon name='chevron-left' color={colors.PALE_SKY} />
        <p
          css={css`
            color: ${colors.PALE_SKY};
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
          `}
        >
          Back
        </p>
      </button>
      <h1
        css={css`
          color: ${isDarkMode ? colors.WHITE : colors.HEADER};
          margin-top: 32px;
          text-align: center;
          font-family: Rift;
          font-size: 40px;
          font-style: normal;
          font-weight: 700;
          line-height: 100%; /* 40px */
          letter-spacing: 0.6px;
          text-transform: uppercase;
        `}
      >
        NEW PLAYER REGISTRATION
      </h1>
      <EventCard eventInfo={eventInfo} isDarkMode={isDarkMode} />
      <div>
        <p
          css={css`
            font-size: 18px;
            font-style: normal;
            font-weight: 700;
            line-height: normal;
            letter-spacing: 0.9px;
            margin: 32px 0;
            color: ${isDarkMode ? colors.WHITE : colors.HEADER};
          `}
        >
          TEAM INFO
        </p>

        {preselectedTeam ? (
          <>
            <p>Your Team</p>
            <div
              css={css`
                background-color: transparent;
                border: 1px solid ${colors.ATTENDANCE_GRAY};
                padding: 16px;
                border-radius: 8px;
                margin: 8px 0;
                width: 100%;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                {preselectedTeam.image?.full_path && (
                  <img
                    src={preselectedTeam.image?.full_path}
                    css={css`
                      width: 24px;
                      height: 24px;
                      margin-right: 8px;
                    `}
                  />
                )}
                {preselectedTeam?.name && (
                  <p
                    css={css`
                      font-size: 16px;
                      font-style: normal;
                      font-weight: 500;
                      line-height: normal;
                      color: ${isDarkMode ? colors.WHITE : '#3f4753'};
                    `}
                  >
                    {preselectedTeam?.name}
                  </p>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <p
              css={css`
                font-size: 14px;
                margin-bottom: 8px;
              `}
            >
              Team Name
            </p>
            <Select
              css={css`
                width: 100%;
              `}
              placeholder='Select...'
              onChange={(e) => {
                const playerTeam = playerTeams.find(
                  (team) => team.uid === e || team.id === e
                )
                setSelectedTeam(playerTeam)
              }}
              options={
                playerTeams
                  ? playerTeams.map((el) => {
                      return {
                        label: el.name,
                        value: el.uid || el.id,
                      }
                    })
                  : []
              }
            />
            {/* <Select
              options={playerTeams ?? []}
              name='team_selector'
              menuIsOpen={teamMenuIsOpen}
              onChange={(e) => e?.id && setSelectedTeam(e)}
              components={{
                Option: (props) => (
                  <TeamOptions
                    {...props}
                    onClick={() => setTeamMenuIsOpen(!teamMenuIsOpen)}
                  />
                ),
                DropdownIndicator: (props) => (
                  <DropdownIndicator
                    {...props}
                    onClick={() => setTeamMenuIsOpen(!teamMenuIsOpen)}
                  />
                ),
                ValueContainer: (props) => (
                  <TeamControl
                    {...props}
                    onClick={() => setTeamMenuIsOpen(!teamMenuIsOpen)}
                  />
                ),
              }}
              styles={{
                container: (baseStyles) => ({
                  ...baseStyles,
                  width: '100%',
                }),
                control: (baseStyles) => ({
                  ...baseStyles,
                  backgroundColor: '#f5f5f5',
                  borderColor: colors.ATTENDANCE_GRAY,
                  minHeight: 40,
                }),
                menu: (baseStyles) => ({
                  ...baseStyles,
                  backgroundColor: '#f5f5f5',
                  borderColor: colors.ATTENDANCE_GRAY,
                }),
                option: (baseStyles, { isFocused }) => ({
                  ...baseStyles,
                  backgroundColor: isFocused
                    ? 'rgba(255, 255, 255, 0.9)'
                    : undefined,
                }),
              }}
            /> */}
          </>
        )}
      </div>
      <div
        css={css`
          margin: 32px 0;
          height: 1px;
          background-color: #dfdfe0;
        `}
      />
      <p
        css={css`
          font-size: 18px;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
          letter-spacing: 0.9px;
          margin: 32px 0;
          color: ${isDarkMode ? colors.WHITE : colors.HEADER};
        `}
      >
        PLAYER CONTACT INFO
      </p>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;

          @media (max-width: 768px) {
            flex-direction: column;
          }
        `}
      >
        <Input
          label='First Name'
          useAsRow
          value={userExtraInfo?.name_first}
          isDarkMode={isDarkMode}
        />
        <Input
          label='Last Name'
          useAsRow
          value={userExtraInfo?.name_last}
          isDarkMode={isDarkMode}
        />
      </div>
      <Input
        label='Email Address'
        value={userExtraInfo?.email}
        useAsRow
        isDarkMode={isDarkMode}
      />
      {userExtraInfo?.phone && (
        <Input
          label='Phone'
          value={userExtraInfo?.phone}
          useAsRow
          isDarkMode={isDarkMode}
        />
      )}

      <div
        css={css`
          display: flex;
          align-items: center;
          width: 100%;

          @media (max-width: 768px) {
            flex-direction: column;

            > div {
              width: 100%;
            }
          }
        `}
      >
        <div
          css={css`
            flex: 1;
            margin-right: 8px;

            @media (max-width: 768px) {
              margin-right: 0;
            }
          `}
        >
          <Input
            value={number}
            placeholder='99'
            inputType='number'
            onChange={(e) => setPlayerNumber(e.target.value)}
            label='Number *'
            isDarkMode={isDarkMode}
          />
        </div>

        <div
          css={css`
            flex: 1.4;
            margin-left: 8px;

            @media (max-width: 768px) {
              margin-left: 0;
              margin-bottom: 8px;
            }
          `}
        >
          <p
            css={css`
              font-size: 14px;
              margin-bottom: 8px;
            `}
          >
            Desired Position *
          </p>

          <Select
            css={css`
              width: 100%;
            `}
            placeholder='Select...'
            onChange={(e) => {
              setPlayerPosition(e)
            }}
            options={[
              { value: 3, label: 'Forward' },
              { value: 2, label: 'Defense' },
              { value: 1, label: 'Goalie' },
              { value: 4, label: 'Skater' },
            ]}
          />
          {/* <Select
            options={[
              { id: 3, name: 'Forward' },
              { id: 2, name: 'Defense' },
              { id: 1, name: 'Goalie' },
              { id: 4, name: 'Skater' },
            ]}
            name='position_selector'
            menuIsOpen={positionMenuIsOpen}
            menuPlacement='auto'
            onChange={(value) => {
              setPlayerPosition(value.id)
            }}
            components={{
              Option: (props) => (
                <SelectOptions
                  {...props}
                  isDarkMode={isDarkMode}
                  onClick={() => setPositionMenuIsOpen(!positionMenuIsOpen)}
                />
              ),
              DropdownIndicator: (props) => (
                <DropdownIndicator
                  {...props}
                  onClick={() => setPositionMenuIsOpen(!positionMenuIsOpen)}
                />
              ),
              ValueContainer: (props) => (
                <SelectControl
                  {...props}
                  onClick={() => setPositionMenuIsOpen(!positionMenuIsOpen)}
                />
              ),
            }}
            styles={{
              container: (baseStyles) => ({
                ...baseStyles,
                width: '100%',
              }),
              control: (baseStyles) => ({
                ...baseStyles,
                backgroundColor: isDarkMode ? '#33363B' : '#f5f5f5',
                borderColor: colors.ATTENDANCE_GRAY,
                minHeight: 40,
              }),
              menu: (baseStyles) => ({
                ...baseStyles,
                backgroundColor: isDarkMode ? '#33363B' : '#f5f5f5',
                borderColor: colors.ATTENDANCE_GRAY,
              }),
              option: (baseStyles, { isFocused }) => ({
                ...baseStyles,
                backgroundColor: isFocused
                  ? 'rgba(255, 255, 255, 0.9)'
                  : undefined,
              }),
            }}
          /> */}
        </div>
        <div
          css={css`
            flex: 1;
            margin-left: 16px;

            @media (max-width: 768px) {
              margin-left: 0;
            }
          `}
        >
          <p
            css={css`
              font-size: 14px;
              margin-bottom: 8px;
            `}
          >
            Birth Date *
          </p>
          <div
            css={css`
              display: flex;
              gap: 4px;
              align-items: center;
              flex: 1;

              @media (max-width: 768px) {
                flex-direction: column;
              }
            `}
          >
            <Input
              value={dateOfBirthday.year}
              placeholder='Year'
              inputType='number'
              isDarkMode={isDarkMode}
              onChange={(e) => {
                if (e.target.value.length <= 4 && Number(e.target.value) < 2023)
                  setPlayerDateOfBirthday({
                    ...dateOfBirthday,
                    year: e.target.value,
                  })
              }}
            />
            <Input
              value={dateOfBirthday.month}
              placeholder='Month'
              inputType='number'
              isDarkMode={isDarkMode}
              onChange={(e) => {
                if (e.target.value.length <= 2 && Number(e.target.value) <= 12)
                  setPlayerDateOfBirthday({
                    ...dateOfBirthday,
                    month: e.target.value,
                  })
              }}
            />
            <Input
              value={dateOfBirthday.day}
              placeholder='Day'
              inputType='number'
              isDarkMode={isDarkMode}
              onChange={(e) => {
                if (e.target.value.length <= 2 && Number(e.target.value) <= 31)
                  setPlayerDateOfBirthday({
                    ...dateOfBirthday,
                    day: e.target.value,
                  })
              }}
            />
          </div>
        </div>
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          flex-direction: column;
          width: 100%;
        `}
      >
        {/* <div
          css={css`
            flex: 2;
            margin-left: 8px;
          `}
        > */}

        {/* <Select
            options={skillLevels ?? []}
            name='skill_selector'
            menuPlacement='auto'
            menuIsOpen={skillMenuIsOpen}
            onChange={(value) => {
              setPlayerSkillLevel(value.id)
            }}
            components={{
              Option: (props) => (
                <SelectOptions
                  {...props}
                  isDarkMode={isDarkMode}
                  onClick={() => setSkillMenuIsOpen(!skillMenuIsOpen)}
                />
              ),
              DropdownIndicator: (props) => (
                <DropdownIndicator
                  {...props}
                  onClick={() => setSkillMenuIsOpen(!skillMenuIsOpen)}
                />
              ),
              ValueContainer: (props) => (
                <SelectControl
                  {...props}
                  onClick={() => setSkillMenuIsOpen(!skillMenuIsOpen)}
                />
              ),
            }}
            styles={{
              container: (baseStyles) => ({
                ...baseStyles,
                width: '100%',
              }),
              control: (baseStyles) => ({
                ...baseStyles,
                backgroundColor: isDarkMode ? '#33363B' : '#f5f5f5',
                borderColor: colors.ATTENDANCE_GRAY,
                minHeight: 40,
              }),
              menu: (baseStyles) => ({
                ...baseStyles,
                backgroundColor: isDarkMode ? '#33363B' : '#f5f5f5',
                borderColor: colors.ATTENDANCE_GRAY,
              }),
              option: (baseStyles, { isFocused }) => ({
                ...baseStyles,
                backgroundColor: isFocused
                  ? 'rgba(255, 255, 255, 0.9)'
                  : undefined,
              }),
            }}
          /> */}
        {/* </div> */}
        <p
          css={css`
            font-size: 14px;
            margin-bottom: 8px;
          `}
        >
          Skill Level *
        </p>

        <Select
          css={css`
            width: 100%;
          `}
          placeholder='Select...'
          onChange={(e) => {
            setPlayerSkillLevel(e)
          }}
          options={
            skillLevels
              ? skillLevels.map((el) => {
                  return {
                    label: el.tier,
                    value: el.uid || el.id,
                  }
                })
              : []
          }
        />
      </div>

      <button
        disabled={disabled}
        onClick={() =>
          confirmPlayerSelect({
            endpointInfo: {
              rosterId:
                selectedTeam?.team_player_roster_id ||
                preselectedTeam?.team_player_roster_id,
              scheduleId: eventInfo?.id,
              teamId: selectedTeam?.id || preselectedTeam?.id,
              teamImageUrl:
                selectedTeam?.image?.full_path ||
                preselectedTeam?.image?.full_path,
              teamName: selectedTeam?.name || preselectedTeam?.name,
            },
            playerInfo: {
              firstName: userExtraInfo?.name_first,
              lastName: userExtraInfo?.name_last,
              email: userExtraInfo?.email,
              dateOfBirthday: `${dateOfBirthday.year}-${dateOfBirthday.month}-${dateOfBirthday.day}`,
              skillLevel: skillLevel,
              number: number,
              position: position,
            },
          })
        }
        css={css`
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
          display: flex;
          opacity: ${disabled ? 0.5 : 1};
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          height: 40px;
          margin-top: 32px;
          border-radius: 8px;
          background-color: ${colors.DEFAULT_FLAIR};
          color: ${colors.WHITE};
          font-size: 18px;
          font-weight: 700;
          font-family: ${isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
            : 'Rift, Arial, Helvetica, sans-serif'};
          transition: all 0.2s ease-in-out;

          &:hover {
            ${!disabled && `background-color: ${colors.DEFAULT_FLAIR_HOVER};`}
          }
        `}
      >
        CONFIRM MY REGISTRATION
      </button>
    </div>
  )
})
